import i18n from "i18next";
import type { FC } from "react";
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";

import { AddEmvKernelApacButton } from "src/app/features/EmvKernelProfile/components/buttons/AddEmvApacButton";
import { AddEmvKernelButton } from "src/app/features/EmvKernelProfile/components/buttons/AddEmvButton";
import { EmvKernelProfilesSearchForm } from "src/app/features/EmvKernelProfile/components/forms/EmvKernelProfilesSearchForm";
import { useUpdateTableDataMutation } from "../../../../components";
import { PageLayout, PageTitle } from "../../../../components/AppLayout/components/Content/components";
import { NoAccessLayout } from "../../../../components/AppLayout/components/Content/components/NoAccessLayout";
import { showSuccessNotification } from "../../../../components/NotificationAlerts";
import type { AlertActionPayload, ExtendedAlertActionPayload } from "../../../../components/NotificationAlerts/types";
import { Actions, ResourceManager } from "../../../../components/ResourceManager/ResourceManager";
import { getAssignedDataGroup } from "../../../../core/router/auth";
import { canAccess } from "../../../../core/router/auth/permissions";
import { ResourceName, ResourceVariant } from "../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../core/router/types/routerTypes";
import type { NoneOwnProps } from "../../../../core/types";
import type { Entity } from "../../../../core/types/fe";
import { EmvKernelProfilesTable } from "../components/tables/EmvKernelProfilesTable";
import type { EmvKernelProfileList } from "../types/EmvKernelProfileList";

export const EmvKernelProfilePage: FC<NoneOwnProps> = () => {
    const table = <EmvKernelProfilesTable />;
    const searchForm = <EmvKernelProfilesSearchForm />;
    const hasAccess = canAccess(ResourceName.EMV_KERNEL_PROFILES, "canRead");

    const [copyRequest, { isLoading }] = useUpdateTableDataMutation();
    const baseURI = `${ResourceName.DATA_GROUPS}/${getAssignedDataGroup()?.code}`;
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const notifyOkCopy = useCallback(
        (payload: ExtendedAlertActionPayload) => {
            dispatch(
                showSuccessNotification({
                    text: i18n.t("common.formCopySuccess", {
                        id: payload.id,
                        resource: payload.name,
                    }),
                    autoCloseDisabled: false,
                }),
            );
        },
        [dispatch],
    );

    const copyCallback = (record: EmvKernelProfileList) => {
        copyRequest({
            url: `${baseURI}/${ResourceName.EMV_KERNEL_PROFILES}/${record.id}/${FORM_ACTION.COPY}`,
            method: "POST",
        })
            .unwrap()
            .then((payload) => performCopySuccess(payload))
            .catch(() => {
                // error handled in rtkQueryErrorLogger
            });

        const performCopySuccess = (payload: AlertActionPayload | undefined) => {
            const payloadIdObject = payload as Entity;
            const route = record.apac
                ? `${BASE_PATH}/${ResourceName.EMV_KERNEL_PROFILES}/${ResourceVariant.APAC}/${FORM_ACTION.EDIT}/${payloadIdObject.id}`
                : `${BASE_PATH}/${ResourceName.EMV_KERNEL_PROFILES}/${FORM_ACTION.EDIT}/${payloadIdObject.id}`;
            navigate(route);
            if (payload) {
                notifyOkCopy(payload);
            }
        };
    };

    return (
        <PageLayout name="emvKernelProfiles_page">
            {hasAccess ? (
                <>
                    <PageTitle translationKey="menu.emvKernelProfiles" />
                    <ResourceManager<EmvKernelProfileList>
                        useDataGroup
                        dataMapperKey="emvKernelProfilesTableData"
                        resource={ResourceName.EMV_KERNEL_PROFILES}
                        table={table}
                        searchForm={searchForm}
                        actionCallbacks={{ copy: copyCallback }}
                        customActions={[
                            {
                                action: <AddEmvKernelButton />,
                                permission: "eft4f",
                            },
                            {
                                action: <AddEmvKernelApacButton />,
                                permission: "xml",
                            },
                        ]}
                        actions={[Actions.DELETE, Actions.COPY, Actions.EXPORT]}
                        isLoading={isLoading}
                    />
                </>
            ) : (
                <NoAccessLayout />
            )}
        </PageLayout>
    );
};
